package top.wcpe.allincore.ui.web.input

internal object DomCodeUiKeyMapper {
    const val MAPPING_VERSION: String = "dom-code-ui-keys-v1"
    const val SCAN_CODE: Int = 0
    const val VANILLA_KEY: String = ""
    const val UNKNOWN: String = ""

    private val namedCodes = mapOf(
        "Escape" to "Escape", "Enter" to "Enter", "NumpadEnter" to "NumpadEnter",
        "Tab" to "Tab", "Backspace" to "Backspace", "Insert" to "Insert", "Delete" to "Delete",
        "Space" to "Space", "ArrowUp" to "Up", "ArrowDown" to "Down", "ArrowLeft" to "Left",
        "ArrowRight" to "Right", "Home" to "Home", "End" to "End", "PageUp" to "PageUp",
        "PageDown" to "PageDown", "NumpadDecimal" to "NumpadDecimal", "NumpadDivide" to "NumpadDivide",
        "NumpadMultiply" to "NumpadMultiply", "NumpadSubtract" to "NumpadSubtract",
        "NumpadAdd" to "NumpadAdd", "NumpadEqual" to "NumpadEqual", "CapsLock" to "CapsLock",
        "ScrollLock" to "ScrollLock", "NumLock" to "NumLock", "PrintScreen" to "PrintScreen",
        "Pause" to "Pause", "ContextMenu" to "Menu", "Minus" to "Minus", "Equal" to "Equal",
        "BracketLeft" to "LeftBracket", "BracketRight" to "RightBracket", "Backslash" to "Backslash",
        "Semicolon" to "Semicolon", "Quote" to "Apostrophe", "Comma" to "Comma",
        "Period" to "Period", "Slash" to "Slash", "Backquote" to "Grave", "ShiftLeft" to "Shift",
        "ShiftRight" to "Shift", "ControlLeft" to "Ctrl", "ControlRight" to "Ctrl", "AltLeft" to "Alt",
        "AltRight" to "Alt", "MetaLeft" to "Super", "MetaRight" to "Super",
    )

    private val fallbackKeys = mapOf(
        "Escape" to "Escape", "Enter" to "Enter", "Tab" to "Tab", "Backspace" to "Backspace",
        "Delete" to "Delete", "ArrowUp" to "Up", "ArrowDown" to "Down", "ArrowLeft" to "Left",
        "ArrowRight" to "Right", "Home" to "Home", "End" to "End", "PageUp" to "PageUp",
        "PageDown" to "PageDown", " " to "Space",
    )

    fun map(code: String, key: String, isComposing: Boolean = false): String {
        if (isComposing) return UNKNOWN
        mapFamily(code)?.let { return it }
        namedCodes[code]?.let { return it }
        if (code.isNotEmpty() && code != "Unidentified") return UNKNOWN
        return fallbackKeys[key] ?: UNKNOWN
    }

    private fun mapFamily(code: String): String? {
        if (code.length == 4 && code.startsWith("Key") && code[3] in 'A'..'Z') return code.substring(3)
        if (code.length == 6 && code.startsWith("Digit") && code[5] in '0'..'9') return code.substring(5)
        parseNumberedCode(code, "F", 1..24)?.let { return "F$it" }
        parseNumberedCode(code, "Numpad", 0..9)?.let { return "Numpad$it" }
        return null
    }

    private fun parseNumberedCode(code: String, prefix: String, range: IntRange): Int? {
        if (!code.startsWith(prefix)) return null
        val number = code.substring(prefix.length).toIntOrNull() ?: return null
        return number.takeIf { it in range }
    }
}

internal data class WebModifiers(
    val shift: Boolean = false,
    val ctrl: Boolean = false,
    val alt: Boolean = false,
    val meta: Boolean = false,
) {
    val bits: Int
        get() = (if (shift) SHIFT_BIT else 0) or
            (if (ctrl) CTRL_BIT else 0) or
            (if (alt) ALT_BIT else 0) or
            (if (meta) META_BIT else 0)

    private companion object {
        const val SHIFT_BIT = 1
        const val CTRL_BIT = 2
        const val ALT_BIT = 4
        const val META_BIT = 8
    }
}

internal enum class WebKeyAction {
    RELEASE,
    PRESS,
    REPEAT,
}

internal enum class WebInputDiagnosticSeverity {
    WARNING,
}

internal data class WebUnknownKeyDiagnostic(
    val code: String = "WEB_INPUT_UNKNOWN_KEY",
    val severity: WebInputDiagnosticSeverity = WebInputDiagnosticSeverity.WARNING,
    val mappingVersion: String = DomCodeUiKeyMapper.MAPPING_VERSION,
    val domCode: String,
    val domKeyClass: String,
    val location: Int,
    val action: WebKeyAction,
    val modifiers: Int,
    val targetPath: String,
    val message: String = "DOM 物理键未登记：$domCode",
)

internal class DeduplicatingWebUnknownKeyDiagnosticSink(private val consumer: (WebUnknownKeyDiagnostic) -> Unit) {
    private val occurrences = mutableMapOf<UnknownKeyIdentity, Int>()

    fun emit(
        sessionId: String,
        domCode: String,
        domKey: String,
        location: Int,
        action: WebKeyAction,
        modifiers: Int,
        targetPath: String,
    ): Boolean {
        val identity = UnknownKeyIdentity(sessionId, DomCodeUiKeyMapper.MAPPING_VERSION, domCode, location)
        val count = (occurrences[identity] ?: 0) + 1
        occurrences[identity] = count
        if (count > 1) return false
        consumer(createDiagnostic(domCode, domKey, location, action, modifiers, targetPath))
        return true
    }

    fun duplicateCount(sessionId: String, domCode: String, location: Int): Int =
        occurrences[UnknownKeyIdentity(sessionId, DomCodeUiKeyMapper.MAPPING_VERSION, domCode, location)] ?: 0

    private fun createDiagnostic(
        domCode: String,
        domKey: String,
        location: Int,
        action: WebKeyAction,
        modifiers: Int,
        targetPath: String,
    ) = WebUnknownKeyDiagnostic(
        domCode = domCode,
        domKeyClass = classifyDomKey(domKey),
        location = location,
        action = action,
        modifiers = modifiers,
        targetPath = targetPath,
    )

    private data class UnknownKeyIdentity(
        val sessionId: String,
        val mappingVersion: String,
        val domCode: String,
        val location: Int,
    )
}

internal fun classifyDomKey(key: String): String = when {
    key == "Dead" -> "dead"
    key == "Process" -> "process"
    key.isEmpty() || key == "Unidentified" -> "unidentified"
    isSingleCodePoint(key) || key.any { it.code > ASCII_MAX } -> "printable"
    else -> "control"
}

private fun isSingleCodePoint(value: String): Boolean {
    if (value.length == 1) return true
    if (value.length != 2) return false
    return value[0].code in 0xD800..0xDBFF && value[1].code in 0xDC00..0xDFFF
}

private const val ASCII_MAX = 0x7F
